import React, { useMemo } from 'react';
import useStyles from '../../theme/useStyles';
import { formatCurrency, formatDate } from '../../utils/format';
import { amountOf, peakOf, percent } from '../../utils/summaryStats';
import {
  KpiCard, KpiGrid, SummaryColumns, TablePanel, BarCell, TotalRow, Badge
} from '../../components/ui';
import { offeringSummary } from './analytics';
import type { DonationLike, CollectionLike } from './analytics';

export interface OfferingSummaryProps {
  donations: DonationLike[];
  collections: CollectionLike[];
  loading?: boolean;
  isDarkMode?: boolean;
}

/** The Offerings module's Summary tab: tiles, then report panels and watch-lists. */
const OfferingSummaryView = ({ donations, collections, loading, isDarkMode }: OfferingSummaryProps) => {
  const t = useStyles(isDarkMode).t;
  const s = useMemo(() => offeringSummary(donations, collections), [donations, collections]);
  const monthPeak = peakOf(s.months, (r) => r.total);
  const donorPeak = peakOf(s.topDonors, (r) => r.total);
  const sundayPeak = peakOf(s.sundays, (r) => r.total);
  const money = (v: number) => formatCurrency(v);

  return (
    <>
      <KpiGrid isDarkMode={isDarkMode}>
        <KpiCard label="This Month" value={money(s.thisMonth)} color={t.income} isDarkMode={isDarkMode} />
        <KpiCard label="Last Month" value={money(s.lastMonth)} color={t.textFaint} isDarkMode={isDarkMode} />
        <KpiCard label={`FY ${s.fy} (${s.fyCount})`} value={money(s.fyTotal)} color={t.income} isDarkMode={isDarkMode} />
        <KpiCard label="Donations (FY)" value={money(s.fyDonations)} color={t.primary} isDarkMode={isDarkMode} />
        <KpiCard label="Collections (FY)" value={money(s.fyCollections)} color={t.warning} isDarkMode={isDarkMode} />
        <KpiCard label={`Sunday School (${s.sundaySchool.count})`} value={money(s.sundaySchool.total)} color={t.info} isDarkMode={isDarkMode} />
        <KpiCard label="Avg. Sunday Collection" value={s.avgSunday === null ? '—' : money(s.avgSunday)} color={t.info} isDarkMode={isDarkMode} />
        <KpiCard label="Named Donors (FY)" value={String(s.donorsThisFy)} color={t.primary} isDarkMode={isDarkMode} />
        <KpiCard label={`All Time (${s.liveCount})`} value={money(s.allTime)} color={t.income} isDarkMode={isDarkMode} />
        <KpiCard label="Unverified Collections" value={String(s.unverified.length)} color={s.unverified.length ? t.warning : t.income} isDarkMode={isDarkMode} />
        <KpiCard label="Voided" value={String(s.voided)} color={s.voided ? t.danger : t.textFaint} isDarkMode={isDarkMode} />
      </KpiGrid>

      <TablePanel
        title="Offerings by Month"
        isDarkMode={isDarkMode}
        loading={loading}
        minWidth="560px"
        columns={[
          { key: 'label', header: 'Month', nowrap: true, render: (r) => <strong>{r.label}</strong> },
          { key: 'bar', header: '', render: (r) => <BarCell value={r.total} max={monthPeak} color={t.income} isDarkMode={isDarkMode} /> },
          { key: 'donations', header: 'Donations', align: 'right', nowrap: true, render: (r) => money(r.donations) },
          { key: 'collections', header: 'Collections', align: 'right', nowrap: true, render: (r) => money(r.collections) },
          { key: 'total', header: 'Total', align: 'right', nowrap: true, render: (r) => <strong>{money(r.total)}</strong> }
        ]}
        rows={s.months}
        rowKey="month"
        emptyMessage="No offerings recorded yet."
        below={<TotalRow label="Last 12 months" value={money(s.months.reduce((n, m) => n + m.total, 0))} color={t.income} isDarkMode={isDarkMode} />}
      />

      <SummaryColumns isDarkMode={isDarkMode}>
        <TablePanel
          title={`Donations by Purpose — FY ${s.fy}`}
          isDarkMode={isDarkMode}
          loading={loading}
          columns={[
            { key: 'name', header: 'Purpose', render: (r) => <strong>{r.name}</strong> },
            { key: 'bar', header: 'Share', render: (r) => <BarCell value={percent(r.total, s.fyDonations)} max={100} showPercent color={t.primary} isDarkMode={isDarkMode} /> },
            { key: 'count', header: 'Gifts', align: 'right' },
            { key: 'total', header: 'Amount', align: 'right', nowrap: true, render: (r) => money(r.total) }
          ]}
          rows={s.byPurpose}
          rowKey="name"
          emptyMessage="No donations this financial year."
          below={s.byPurpose.length ? <TotalRow label="Total donations" value={money(s.fyDonations)} color={t.primary} isDarkMode={isDarkMode} /> : null}
        />
        <TablePanel
          title={`Collections by Source — FY ${s.fy}`}
          isDarkMode={isDarkMode}
          loading={loading}
          columns={[
            { key: 'name', header: 'Source', render: (r) => <strong>{r.name}</strong> },
            { key: 'bar', header: 'Share', render: (r) => <BarCell value={percent(r.total, s.fyCollections)} max={100} showPercent color={t.warning} isDarkMode={isDarkMode} /> },
            { key: 'count', header: 'Counts', align: 'right' },
            { key: 'total', header: 'Amount', align: 'right', nowrap: true, render: (r) => money(r.total) }
          ]}
          rows={s.bySource}
          rowKey="name"
          emptyMessage="No collections this financial year."
          below={s.bySource.length ? <TotalRow label="Total collections" value={money(s.fyCollections)} color={t.warning} isDarkMode={isDarkMode} /> : null}
        />
      </SummaryColumns>

      <SummaryColumns isDarkMode={isDarkMode}>
        <TablePanel
          title={`Top Donors — FY ${s.fy}`}
          isDarkMode={isDarkMode}
          loading={loading}
          columns={[
            { key: 'name', header: 'Donor', render: (r) => <strong>{r.name}</strong> },
            { key: 'bar', header: '', render: (r) => <BarCell value={r.total} max={donorPeak} color={t.primary} isDarkMode={isDarkMode} /> },
            { key: 'count', header: 'Gifts', align: 'right' },
            { key: 'total', header: 'Amount', align: 'right', nowrap: true, render: (r) => money(r.total) }
          ]}
          rows={s.topDonors}
          rowKey="name"
          emptyMessage="No named donors this financial year."
        />
        <TablePanel
          title="Recent Sundays — Last 12 Weeks"
          isDarkMode={isDarkMode}
          loading={loading}
          columns={[
            { key: 'date', header: 'Sunday', nowrap: true, render: (r) => <strong>{formatDate(r.date)}</strong> },
            { key: 'bar', header: '', render: (r) => <BarCell value={r.total} max={sundayPeak} color={t.warning} isDarkMode={isDarkMode} /> },
            { key: 'count', header: 'Counts', align: 'right' },
            { key: 'total', header: 'Collected', align: 'right', nowrap: true, render: (r) => money(r.total) }
          ]}
          rows={s.sundays}
          rowKey="date"
          emptyMessage="No Sunday collections in the last 12 weeks."
          below={s.avgSunday !== null ? <TotalRow label="Average per Sunday" value={money(s.avgSunday)} color={t.warning} isDarkMode={isDarkMode} /> : null}
        />
      </SummaryColumns>

      <TablePanel
        title="Collections Not Yet Verified"
        isDarkMode={isDarkMode}
        loading={loading}
        minWidth="520px"
        columns={[
          { key: 'receiptId', header: 'Receipt', nowrap: true, render: (r) => <strong>{r.receiptId || '-'}</strong> },
          { key: 'date', header: 'Date', nowrap: true, render: (r) => formatDate(r.date) },
          { key: 'source', header: 'Source', render: (r) => <Badge tone="neutral">{r.source || '-'}</Badge> },
          { key: 'countedBy', header: 'Counted By', render: (r) => r.countedBy || '-' },
          { key: 'amount', header: 'Amount', align: 'right', nowrap: true, render: (r) => <strong>{money(amountOf(r.amount))}</strong> }
        ]}
        rows={s.unverified}
        rowKey={(r) => `${r.receiptId}|${r.date}|${r.source}`}
        emptyMessage="Every collection has been verified by a second person."
      />
    </>
  );
};

export default OfferingSummaryView;
